import React, { useState, useRef, useEffect, FC } from 'react'

interface SelectionByMouseDragProps {
  items?: any[]
  itemsRef?: any
  handleSelectionByMouseDrag?: (selected, flag) => void
  canDragAndDrop?: boolean
  className?: any
  containerRef?: any
  setIsWindowSelectionGoingOn?: (flag) => void
  setWindowSelectionHeaderPosition?: (flag) => void
  windowSelectionHeaderPosition?: number
  headerOffsetHeight?: number
  isRightHandSideOperation?: boolean
}

const SelectionByMouseDrag: FC<SelectionByMouseDragProps> = ({
  children,
  items,
  itemsRef,
  handleSelectionByMouseDrag,
  canDragAndDrop,
  className,
  containerRef,
  setIsWindowSelectionGoingOn,
  setWindowSelectionHeaderPosition,
  windowSelectionHeaderPosition,
  headerOffsetHeight,
  isRightHandSideOperation
}) => {
  const [selectionBox, setSelectionBox] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    scrollTop: 0
  }) // Ensure it's always an object
  const [isMouseUp, setIsMouseUp] = useState(false)
  const scrollAnimationFrameRef = useRef(null) // Track the requestAnimationFrame ID
  const isScrolling = useRef(false) // Flag to indicate whether scrolling should continue
  const ITEM_THRESHOLD = 20
  useEffect(() => {
    if (isMouseUp) {
      // Get selected items by checking if their bounding boxes are inside the selection box
      const itemContainerRef = containerRef?.current
      const containerPosition = itemContainerRef?.getBoundingClientRect()

      const updatedSelectionY =
        selectionBox?.scrollTop &&
        windowSelectionHeaderPosition &&
        windowSelectionHeaderPosition <= headerOffsetHeight / 2
          ? selectionBox?.y + headerOffsetHeight
          : selectionBox?.y
      const selected = items.filter((item, index) => {
        const itemElement = itemsRef.current[index]
        if (itemElement) {
          const itemPosition = itemElement.getBoundingClientRect() // Get the item's position and size
          return (
            Math.round(itemPosition.left - containerPosition?.x) >=
              Math.round(selectionBox?.x - ITEM_THRESHOLD) &&
            Math.round(
              itemPosition.top + itemContainerRef?.scrollTop - containerPosition?.y
            ) >= Math.round(updatedSelectionY - ITEM_THRESHOLD) &&
            Math.round(itemPosition.right - containerPosition?.x) <=
              Math.round(selectionBox?.x + selectionBox?.width + ITEM_THRESHOLD) &&
            Math.round(
              itemPosition.bottom + itemContainerRef?.scrollTop - containerPosition?.y
            ) <= Math.round(selectionBox?.y + selectionBox?.height + ITEM_THRESHOLD)
          )
        }
        return false
      })
      setIsMouseUp(false)
      setWindowSelectionHeaderPosition(0)
      handleSelectionByMouseDrag(selected, isRightHandSideOperation)
      setSelectionBox({
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        scrollTop: 0
      }) // Clear the selection box after mouseup
    }
  }, [
    isMouseUp,
    items,
    handleSelectionByMouseDrag,
    itemsRef,
    selectionBox,
    containerRef,
    setWindowSelectionHeaderPosition,
    windowSelectionHeaderPosition,
    headerOffsetHeight,
    isRightHandSideOperation
  ])

  // Function to handle mouse down event
  const handleMouseDown = e => {
    const clickedInsideAnyChip = items?.some(
      (item, index) =>
        itemsRef?.current?.[index] && itemsRef?.current?.[index]?.contains(e?.target)
    )
    if (!clickedInsideAnyChip && items?.length) {
      setIsWindowSelectionGoingOn(true)
      const itemContainerRef = containerRef?.current
      const containerPosition = itemContainerRef.getBoundingClientRect()
      const startX = Math.round(e.clientX - containerPosition?.x)
      const startY = Math.round(
        e.clientY + itemContainerRef?.scrollTop - containerPosition?.y
      )
      const intialScrollTop = itemContainerRef?.scrollTop

      // Create selection box while dragging the mouse
      const onMouseMove = moveEvent => {
        if (itemContainerRef) {
          const { scrollTop, scrollHeight, clientHeight } = itemContainerRef
          const width = Math.round(moveEvent.clientX - e.clientX)
          const height = Math.round(moveEvent.clientY - e.clientY)
          const movingScrollTop = Math.round(scrollTop - intialScrollTop)
          const containerBottom = containerPosition.bottom

          // Check if mouse is near the bottom of the div
          const isNearBottom =
            moveEvent.clientY >= containerBottom - ITEM_THRESHOLD &&
            moveEvent.clientY <= containerBottom

          // Check if mouse is inside the container (to prevent scrolling when mouse is outside)
          const isInsideContainer =
            moveEvent.clientY >= containerPosition.top &&
            moveEvent.clientY <= containerPosition.bottom
          // If we are at the bottom, start scrolling
          if (
            isNearBottom &&
            isInsideContainer &&
            scrollTop + clientHeight < scrollHeight
          ) {
            startScrolling() // Start scrolling when near bottom
          } else {
            stopScrolling()
          }

          setSelectionBox({
            x: width < 0 ? moveEvent.clientX - containerPosition?.x : startX,
            y: height < 0 ? moveEvent.clientY - containerPosition?.y + scrollTop : startY,
            width: Math.abs(width),
            height: Math.abs(height + movingScrollTop),
            scrollTop
          })
        }
      }

      // Function to start scrolling
      const startScrolling = () => {
        if (!isScrolling.current) {
          isScrolling.current = true
          scrollContainer() // Start scrolling
        }
      }

      // Function to stop scrolling
      const stopScrolling = () => {
        isScrolling.current = false // Stop scrolling
        cancelAnimationFrame(scrollAnimationFrameRef.current) // Cancel any ongoing animation frame
      }

      // Function to scroll the container continuously
      const scrollContainer = () => {
        const container = containerRef?.current
        if (container) {
          // Scroll the container down by `scrollSpeed`
          container.scrollBy({
            top: 5,
            behavior: 'smooth'
          })
        }
        if (isScrolling.current) {
          scrollAnimationFrameRef.current = requestAnimationFrame(scrollContainer) // Schedule the next frame
        }
      }

      // Handle mouse up to finalize selection
      const onMouseUp = moveEvent => {
        stopScrolling()
        setIsMouseUp(true)
        // Clean up event listeners
        window.removeEventListener('mousemove', onMouseMove)
        window.removeEventListener('mouseup', onMouseUp)
      }

      // Attach mousemove and mouseup event listeners
      window.addEventListener('mousemove', onMouseMove)
      window.addEventListener('mouseup', onMouseUp)
    }
  }
  return (
    <div
      ref={containerRef}
      className={className}
      onMouseDown={!canDragAndDrop ? handleMouseDown : null}
    >
      {/* Render the items */}
      {children}

      {/* Render the selection box */}
      {selectionBox && selectionBox.width !== 0 && selectionBox.height !== 0 && (
        <div
          style={{
            position: 'absolute',
            left: `${selectionBox.x}px`,
            top: `${selectionBox.y}px`,
            width: `${selectionBox.width}px`,
            height: `${selectionBox.height}px`,
            overflow: 'hidden',
            border: '1px solid #999999',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 10
          }}
        />
      )}
    </div>
  )
}

export default SelectionByMouseDrag
